import React, { useEffect, useState } from 'react';
import HeaderMainPage from '../../header/HeaderMainPage';
import Footer from '../homepage/Footer';
import { CheckCircle, Sparkles, Rocket, Zap, Building2, Dot } from 'lucide-react';

/**
 * BillingPage — Redesigned from provided spec (router-safe)
 * ------------------------------------------------------------------
 * - Based on your provided layout but visually distinct from homepage
 * - New iconography per tier, updated fonts/weights, and tweaked palette
 * - Currency pill selector retained; improved contrast & accents
 * - Preserves checkout mapping (0..2) and router-safe design
 * ------------------------------------------------------------------
 */


const pricingData = {
  currencies: [
    { id: 'USD', symbol: '$', name: 'USA (USD)', flag: '🇺🇸' },
    { id: 'GBP', symbol: '£', name: 'UK (GBP)', flag: '🇬🇧' },
    { id: 'CAD', symbol: 'C$', name: 'Canada (CAD)', flag: '🇨🇦' },
    { id: 'AUD', symbol: 'A$', name: 'Australia (AUD)', flag: '🇦🇺' },
    { id: 'INR', symbol: '₹', name: 'India (INR)', flag: '🇮🇳' },
  ],
  plans: [
    {
      id: 1,
      title: 'Starter',
      credits: '500',
      prices: { INR: '3,499', USD: '49', GBP: '39.99', CAD: '69.99', AUD: '79.99' },
      vat: { GBP: true },
      features: ['50 Companies OR', '20 Business Proposals', '100 Voice Notes', '250 Emails/Messages'],
      mixExample: {
        title: 'Mix Example:',
        items: ['20 intent signals', '50 emails', '30 voice notes', '2 proposals'],
      },
      recommended: false,
      icon: 'sparkles',
      accent: 'from-violet-500 to-indigo-600',
    },
    {
      id: 2,
      title: 'Growth',
      credits: '1,000',
      prices: { INR: '6,999', USD: '99', GBP: '79.99', CAD: '139.99', AUD: '159.99' },
      vat: { GBP: true },
      features: ['100 Companies OR', '40 Business Proposals', '200 Voice Notes', '500 Emails/Messages'],
      mixExample: {
        title: 'Mix Example:',
        items: ['40 intent signals', '100 emails', '60 voice notes', '4 proposals'],
      },
      recommended: true,
      icon: 'rocket',
      accent: 'from-indigo-600 to-blue-600',
    },
    {
      id: 3,
      title: 'Pro',
      credits: '2,000',
      prices: { INR: '13,999', USD: '199', GBP: '159.99', CAD: '279.99', AUD: '319.99' },
      vat: { GBP: true },
      features: ['200 Companies OR', '80 Business Proposals', '400 Voice Notes', '1000 Emails/Messages'],
      mixExample: {
        title: 'Mix Example:',
        items: ['80 intent signals', '200 emails', '120 voice notes', '8 proposals'],
      },
      recommended: false,
      icon: 'zap',
      accent: 'from-sky-600 to-cyan-600',
    },
    {
      id: 4,
      title: 'Enterprise',
      credits: 'Custom',
      prices: { INR: 'Custom', USD: 'Custom', GBP: 'Custom', CAD: 'Custom', AUD: 'Custom' },
      priceInfo: 'Starts at $499+',
      features: ['Custom Credit Amount', 'Dedicated Account Manager', 'API Access & Support', 'Custom Onboarding & Training'],
      recommended: false,
      icon: 'building',
      accent: 'from-slate-700 to-slate-900',
    },
  ],
};

// Map plan title -> checkout index to preserve existing backend mapping
const CHECKOUT_INDEX = { Starter: 0, Growth: 1, Pro: 2 };

// Lightweight skeleton used during loading
function SkeletonCard() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="w-[290px] md:w-[300px] rounded-2xl border border-slate-200 bg-white shadow-xl animate-pulse p-6"
    >
      <div className="h-8 w-2/3 rounded-full bg-slate-200" />
      <div className="mt-3 h-6 w-1/2 rounded-full bg-slate-200" />
      <div className="mt-6 h-[180px] rounded-xl bg-slate-100" />
      <div className="mt-6 h-11 w-full rounded-full bg-slate-200" />
    </div>
  );
}

// Small helper to render the tier icon
function TierIcon({ name, className = 'h-5 w-5' }) {
  if (name === 'sparkles') return <Sparkles className={className} />;
  if (name === 'rocket') return <Rocket className={className} />;
  if (name === 'zap') return <Zap className={className} />;
  if (name === 'building') return <Building2 className={className} />;
  return <Dot className={className} />;
}

function CurrencyPills({ selected, onSelect }) {
  const count = pricingData.currencies.length;
  const activeIdx = Math.max(
    0,
    pricingData.currencies.findIndex((c) => c.id === selected)
  );
  const widthCss = `calc((100% - 1rem) / ${count})`;
  const leftCss = `calc(${activeIdx} * ((100% - 1rem) / ${count}) + 0.5rem)`;

  return (
    <div className="mt-10 flex justify-center">
      <div className="relative w-full max-w-3xl select-none">
        {/* Track */}
        <div className="relative rounded-full border border-slate-300/70 bg-white/40 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] backdrop-blur-md px-2 py-2">
          {/* Animated indicator */}
          <div
            aria-hidden
            className="absolute top-2 bottom-2 rounded-full bg-white shadow-md ring-1 ring-slate-200 transition-all duration-300 ease-out"
            style={{ width: widthCss, left: leftCss }}
          />

          <div className="relative grid grid-cols-5 gap-0">
            {pricingData.currencies.map((currency) => {
              const isActive = selected === currency.id;
              const name = currency.name;
              const country = name.split(' ')[0];
              const code = name.match(/\((.*?)\)/)?.[1] || '';

              return (
                <button
                  key={currency.id}
                  onClick={() => onSelect(currency.id)}
                  className="relative z-10 w-full flex items-center justify-center gap-3 rounded-full px-3 py-3 transition-colors duration-200 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-300/60"
                  aria-pressed={isActive}
                  aria-label={`${country} (${code})`}
                >
                  {/* Flag */}
                  <span
                    className={[
                      'flex h-10 w-10 items-center justify-center rounded-full border text-lg shadow-sm',
                      isActive
                        ? 'bg-white border-slate-300'
                        : 'bg-white/80 border-slate-200'
                    ].join(' ')}
                  >
                    {currency.flag}
                  </span>

                  {/* Labels */}
                  <span className="text-left leading-tight">
                    <span className={[
                      'block text-[13px] font-semibold tracking-wide',
                      isActive ? 'text-indigo-700' : 'text-slate-700'
                    ].join(' ')}>
                      {country}
                    </span>
                    <span className={[
                      'block text-[11px] font-medium uppercase',
                      isActive ? 'text-indigo-600' : 'text-slate-500'
                    ].join(' ')}>
                      {code}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Soft top highlight */}
        <div aria-hidden className="pointer-events-none absolute -top-2 left-6 right-6 h-6 rounded-full bg-white/50 blur-md" />
      </div>
    </div>
  );
}

function Card({ plan, selectedCurrency, onBuy }) {
  const symbol = pricingData.currencies.find((c) => c.id === selectedCurrency)?.symbol || '';
  const price = plan.prices[selectedCurrency];
  const isCustom = String(price).toLowerCase() === 'custom';

  return (
    <div
      className={`relative rounded-2xl p-6 transition-all duration-300 flex flex-col h-full border shadow-lg bg-gradient-to-b from-white to-slate-50 ${
        plan.recommended
          ? 'border-indigo-500 ring-2 ring-indigo-200 scale-[1.02] hover:ring-indigo-300'
          : 'border-slate-200 hover:shadow-xl'
      }`}
    >
      {/* Accent corner */}
      <div className={`pointer-events-none absolute -top-6 -right-6 h-24 w-24 rounded-full blur-2xl opacity-20 bg-gradient-to-br ${plan.accent}`} />

      {plan.recommended && (
        <p className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-[11px] font-semibold px-3 py-1 rounded-full uppercase tracking-wider shadow">
          Most Popular
        </p>
      )}

      <div className="text-center">
        <div className="mx-auto mb-3 inline-flex items-center justify-center rounded-xl p-2 bg-slate-100 text-slate-700">
          <TierIcon name={plan.icon} className="h-6 w-6" />
        </div>
        <h3 className="text-[1.35rem] font-extrabold tracking-tight text-slate-900">{plan.title}</h3>
        <p className="mt-3 text-4xl font-black tabular-nums text-slate-900">
          {!isCustom && symbol}
          {price}
          {plan.vat && plan.vat[selectedCurrency] && (
            <span className="ml-1 align-super text-sm font-medium text-slate-400">+VAT</span>
          )}
        </p>
        <p className="mt-1 text-base font-semibold text-indigo-600">{plan.credits} Credits</p>
      </div>

      {Array.isArray(plan.features) && plan.features.length > 0 && (
        <div className={"mt-6 space-y-2 border-t border-slate-200 pt-6 text-left"}>
          {plan.features.map((feat, idx) => (
            <p key={idx} className={`flex items-start ${plan.title === 'Enterprise' ? '' : ''}`}>
              {plan.title === 'Enterprise' ? (
                <CheckCircle className="h-5 w-5 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
              ) : (
                <Dot className="h-5 w-5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
              )}
              <span className="text-slate-700 text-[0.95rem] leading-6">{feat}</span>
            </p>
          ))}
        </div>
      )}

      {plan.mixExample && (
        <div className="mt-6 space-y-2 border-t border-slate-200 pt-6 text-left">
          <p className="font-bold text-slate-800 text-sm">{plan.mixExample.title}</p>
          {plan.mixExample.items.map((item, idx) => (
            <p key={idx} className="text-slate-600 text-[0.9rem]">{item}</p>
          ))}
        </div>
      )}

      {plan.priceInfo && <p className="text-left text-sm text-slate-500 mt-4">{plan.priceInfo}</p>}

      <div className="mt-auto pt-8">
        {isCustom ? (
          <a
            href="mailto:sales@roboman.ai"
            className="w-full inline-flex items-center justify-center gap-2 text-center py-3 px-6 rounded-lg font-semibold transition-colors duration-300 bg-slate-900 text-white hover:bg-black"
          >
            <Building2 className="h-4 w-4" /> Contact Sales
          </a>
        ) : (
          <button
            onClick={onBuy}
            className={`w-full inline-flex items-center justify-center gap-2 py-3 px-6 rounded-lg font-semibold transition-colors duration-300 ${
              plan.recommended
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
            }`}
          >
            <Zap className="h-4 w-4" /> Buy Credits
          </button>
        )}
      </div>
    </div>
  );
}

export default function BillingPage() {
  const roboman_api = process.env.REACT_APP_ROBOMAN_API;

  const [stripePrices, setStripePrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');

  useEffect(() => {
    const getStripePrices = async () => {
      setFetchError(null);

      if (!roboman_api) {
        setFetchError('API base URL (REACT_APP_ROBOMAN_API) is not configured.');
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('access_token');
        const headers = { Accept: 'application/json' };
        if (token) headers['Authorization'] = token;

        const response = await fetch(`${roboman_api}/stripe/prices`, {
          method: 'GET',
          headers,
        });

        const data = await response.json();

        if (!response.ok) {
          console.error('Request failed:', data);
          setFetchError('Unable to load pricing right now.');
        } else {
          setStripePrices(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Network or parsing error:', error);
        setFetchError('Network error while fetching pricing.');
      } finally {
        setLoading(false);
      }
    };

    getStripePrices();
    document.body.classList.add('overflow-x-hidden');
    return () => document.body.classList.remove('overflow-x-hidden');
  }, [roboman_api]);

  const createPaymentSession = async (payment_id) => {
    try {
      if (!roboman_api) return;

      const token = localStorage.getItem('access_token');
      const headers = { Accept: 'application/json', 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = token;

      const response = await fetch(`${roboman_api}/stripe/checkout/session/create`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          price_id: stripePrices[payment_id]?.['price_id'],
          cancel_url: 'https://outreach.roboman.ai/dashboard',
          success_url: 'https://outreach.roboman.ai/successpayment?session_id={CHECKOUT_SESSION_ID}',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Checkout session creation failed', data);
      } else if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Network or parsing error:', error);
    }
  };

  return (
    <div className="min-h-screen w-full bg-slate-50">
      {/* Header */}
      <div className="relative z-30">
        <HeaderMainPage />
      </div>

      {/* Content */}
      <main className="relative z-10 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold text-slate-900">Flexible Credit Packages</h2>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-slate-500">
              Choose a package that fits your outreach needs. One-time purchase, no subscriptions.
            </p>
            <CurrencyPills selected={selectedCurrency} onSelect={setSelectedCurrency} />
          </div>

          {/* Loading */}
          {loading && (
            <div className="grid lg:grid-cols-4 gap-8 items-start justify-items-center">
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          )}

          {/* Error */}
          {!loading && fetchError && (
            <div className="mx-auto max-w-xl rounded-xl border border-amber-300/50 bg-amber-50 p-4 text-amber-900 shadow">
              {fetchError}
            </div>
          )}

          {/* Plans */}
          {!loading && !fetchError && (
            <div className="grid lg:grid-cols-4 gap-8 items-start">
              {pricingData.plans.map((plan) => (
                <Card
                  key={plan.id}
                  plan={plan}
                  selectedCurrency={selectedCurrency}
                  onBuy={() => {
                    const idx = CHECKOUT_INDEX[plan.title];
                    if (idx !== undefined) createPaymentSession(idx);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <div className="relative z-10">
        <Footer />
      </div>
    </div>
  );
}


/*
  Changes updated:
  - New file created: NewBillingPage.js (renamed to BillingPage.js)
  - Redesigned the billing page with a new layout and styles
  ==============================================================
  TESTS (Jest + React Testing Library)
  Save as: BillingPage.test.jsx
  ==============================================================

  import React from 'react';
  import { render, screen, waitFor, fireEvent } from '@testing-library/react';
  import BillingPage from './BillingPage';

  // Mock Header/Footer to isolate the page UI
  jest.mock('../../header/HeaderMainPage', () => () => <div>Header</div>);
  jest.mock('../homepage/Footer', () => () => <div>Footer</div>);

  describe('BillingPage', () => {
    beforeEach(() => {
      jest.resetAllMocks();
      global.fetch = jest.fn(() => Promise.resolve({
        ok: true,
        json: () => Promise.resolve([]),
      }));
    });

    test('renders without Router and shows skeletons', () => {
      render(<BillingPage />);
      expect(screen.getAllByRole('status', { hidden: true }).length).toBeGreaterThan(0);
    });

    test('currency pills switch price labels', async () => {
      render(<BillingPage />);
      await waitFor(() => expect(fetch).toHaveBeenCalled());
      fireEvent.click(screen.getByRole('button', { name: /UK \(GBP\)/i }));
      // Growth GBP price from provided data
      expect(screen.getByText('79.99')).toBeInTheDocument();
    });

    test('shows error banner when API base is missing', async () => {
      const prev = process.env.REACT_APP_ROBOMAN_API;
      delete process.env.REACT_APP_ROBOMAN_API;
      render(<BillingPage />);
      await waitFor(() => screen.getByText(/API base URL/i));
      expect(screen.getByText(/API base URL/i)).toBeInTheDocument();
      process.env.REACT_APP_ROBOMAN_API = prev;
    });
  });
*/
